// ============================================================================
// FSA ROLL-FORWARD CONFIRM MODAL  (FSA-2.3)
// ============================================================================
// Auto-fills period dates + carried settings + signature panel + custom
// classes from a prior FSA engagement. Single "Create" button creates a
// new basic-tier engagement + FSA settings + applies the carry steps in
// one server transaction.
//
// Surfaced from:
//   • FsaEngagementScreen's General/Basic tab — "Create Next Year" action
//   • Basic-tier Engagement list row action (when row's FSA engagement is ISSUED)
//   • FSA Engagements list row action (same)
//
// The 4-tab Create New wizard (for new clients / migration / first year of
// operations) is a separate component shipping in a follow-up session.

import React, { useCallback, useEffect, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import notificationService from 'services/notificationService';
import fsaEngagementService from 'api/services/fsa/fsaEngagementService';
import type { ID } from 'api/types';
import type {
  FsEngagementCreationSpec,
  FsEngagementCreationResult,
  DuplicateEngagementCheck,
} from 'api/types/fsa/rollForward.types';

interface FsaRollForwardConfirmModalProps {
  isOpen: boolean;
  /** The ISSUED engagement to roll forward FROM. */
  priorEngagementId: ID | null;
  /** Display labels for the dialog summary card. */
  priorEngagementCode?: string | null;
  priorClientName?: string | null;
  onClose: () => void;
  /** Called after successful creation with the new engagement's basic-tier id. */
  onCreated?: (result: FsEngagementCreationResult) => void;
  /** Called when the user clicks "Open existing instead" on the duplicate warning. */
  onOpenExisting?: (existingEngagementId: ID) => void;
}

const FsaRollForwardConfirmModal: React.FC<FsaRollForwardConfirmModalProps> = ({
  isOpen,
  priorEngagementId,
  priorEngagementCode,
  priorClientName,
  onClose,
  onCreated,
  onOpenExisting,
}) => {
  const [loading, setLoading] = useState(false);
  const [spec, setSpec] = useState<FsEngagementCreationSpec | null>(null);
  const [duplicate, setDuplicate] = useState<DuplicateEngagementCheck | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [isCreating, setIsCreating] = useState(false);
  const [createConfirmOpen, setCreateConfirmOpen] = useState(false);

  // Load roll-forward spec + duplicate check in parallel on open.
  useEffect(() => {
    let cancelled = false;
    if (!isOpen || !priorEngagementId) return;

    setLoading(true);
    setSpec(null);
    setDuplicate(null);
    setLoadError(null);

    Promise.all([
      fsaEngagementService.getRollForwardSpec(priorEngagementId),
      fsaEngagementService.checkDuplicate(priorEngagementId),
    ])
      .then(([specResp, dupResp]) => {
        if (cancelled) return;
        setSpec(specResp.data ?? null);
        setDuplicate(dupResp.data ?? null);
      })
      .catch((err: any) => {
        if (cancelled) return;
        const msg = err?.response?.data?.message ?? err?.message ?? 'Failed to load roll-forward details.';
        setLoadError(msg);
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [isOpen, priorEngagementId]);

  const handleCreateClick = useCallback(() => {
    if (!spec) return;
    setCreateConfirmOpen(true);
  }, [spec]);

  const handleConfirmCreate = useCallback(async () => {
    if (!spec) return;
    setCreateConfirmOpen(false);
    setIsCreating(true);
    try {
      const response = await fsaEngagementService.createEngagement(spec);
      const result = response.data;
      setIsCreating(false);
      notificationService.toast.success(
        result?.message ?? 'Engagement created successfully.',
      );
      if (result) onCreated?.(result);
      onClose();
    } catch (err: any) {
      setIsCreating(false);
      const data = err?.response?.data;
      const errors = data?.errors;
      const errorMsg = data?.message
        ?? (Array.isArray(errors) && errors.length ? errors.join('; ') : null)
        ?? err?.message
        ?? 'Failed to create engagement.';
      // Log full payload to aid debugging when message is opaque.
      // eslint-disable-next-line no-console
      console.error('[Roll Forward] create failed', { status: err?.response?.status, data, err });
      notificationService.toast.error(errorMsg, 8000);
    }
  }, [spec, onCreated, onClose]);

  const handleOpenExistingClick = useCallback(() => {
    if (!duplicate) return;
    onOpenExisting?.(duplicate.existingEngagementId);
    onClose();
  }, [duplicate, onOpenExisting, onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" onClick={isCreating ? undefined : onClose} />
      <div className="modal-container modal-md">
        {isCreating && (
          <div style={{
            position: 'absolute', inset: 0,
            background: 'var(--modal-loading-overlay)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
          }}>
            <div style={{
              width: '2rem', height: '2rem',
              border: '3px solid var(--border-color)', borderTopColor: '#2563eb',
              borderRadius: '50%', animation: 'spin 0.8s linear infinite',
            }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
              Creating engagement...
            </span>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <IconSystem.arrowRight size={18} />
            <h2 className="modal-title" style={{ margin: 0 }}>
              Create Next Year Engagement
            </h2>
          </div>
          <button className="modal-close" onClick={onClose} disabled={isCreating}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

          {loading && (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: '0.75rem', padding: '2rem',
            }}>
              <div style={{
                width: '1.25rem', height: '1.25rem',
                border: '2px solid var(--border-color)', borderTopColor: '#2563eb',
                borderRadius: '50%', animation: 'spin 0.8s linear infinite',
              }} />
              <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                Loading roll-forward details…
              </span>
            </div>
          )}

          {loadError && !loading && (
            <div style={{
              padding: '1rem', borderRadius: 4,
              background: 'rgba(220, 38, 38, 0.08)', border: '1px solid rgba(220, 38, 38, 0.3)',
              color: 'var(--text-primary)', fontSize: '0.875rem',
            }}>
              <strong>Could not load:</strong> {loadError}
            </div>
          )}

          {!loading && !loadError && spec && (
            <>
              {/* Duplicate warning banner. */}
              {duplicate && (
                <div style={{
                  padding: '0.75rem 1rem', borderRadius: 4,
                  background: 'rgba(217, 119, 6, 0.08)', border: '1px solid rgba(217, 119, 6, 0.3)',
                  display: 'flex', flexDirection: 'column', gap: '0.5rem',
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                    <IconSystem.alert size={16} />
                    <div style={{ fontSize: '0.875rem', color: 'var(--text-primary)' }}>
                      <strong>A next-year FSA engagement already exists:</strong>
                      <div style={{ marginTop: '0.25rem', color: 'var(--text-secondary)' }}>
                        {duplicate.existingEngagementCode} ({duplicate.existingStatusCode}) — period {duplicate.existingPeriodStart?.slice(0, 10)} to {duplicate.existingPeriodEnd?.slice(0, 10)}
                      </div>
                    </div>
                  </div>
                  <div>
                    <button className="btn btn-secondary" style={{ fontSize: '0.8125rem' }}
                            onClick={handleOpenExistingClick}>
                      Open existing instead
                    </button>
                  </div>
                </div>
              )}

              {/* Engagement summary card. */}
              <div style={{
                padding: '1rem', borderRadius: 4,
                background: 'var(--bg-secondary)', border: '1px solid var(--border-color)',
              }}>
                <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em',
                              color: 'var(--text-secondary)', fontWeight: 600, marginBottom: '0.5rem' }}>
                  New engagement
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '0.5rem 1rem',
                              fontSize: '0.875rem', color: 'var(--text-primary)' }}>
                  <span style={{ color: 'var(--text-secondary)' }}>Period</span>
                  <span style={{ fontWeight: 500 }}>
                    {spec.periodStart?.slice(0, 10)} → {spec.periodEnd?.slice(0, 10)}
                  </span>
                  <span style={{ color: 'var(--text-secondary)' }}>FY end</span>
                  <span>{spec.financialYearEnd?.slice(0, 10)}</span>
                  {priorClientName && <>
                    <span style={{ color: 'var(--text-secondary)' }}>Client</span>
                    <span>{priorClientName}</span>
                  </>}
                </div>
              </div>

              {/* Carry-forward summary. */}
              <div style={{
                padding: '1rem', borderRadius: 4,
                background: 'rgba(37, 99, 235, 0.05)', border: '1px solid rgba(37, 99, 235, 0.2)',
              }}>
                <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em',
                              color: 'var(--text-secondary)', fontWeight: 600, marginBottom: '0.5rem' }}>
                  Roll forward from {priorEngagementCode || 'prior engagement'}
                </div>
                <ul style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '0.875rem',
                             color: 'var(--text-primary)', lineHeight: 1.7 }}>
                  <li>Engagement settings (template, currency, rounding factor)</li>
                  <li>Signature panel (audit firm, partners, directors)</li>
                  <li>Custom classes (template extensions added during prior audit)</li>
                  <li>Link to prior engagement (drives auto-classify and per-note Import)</li>
                </ul>
              </div>

              <div style={{ padding: '0.75rem 1rem', background: 'var(--bg-secondary)',
                            borderRadius: 4, fontSize: '0.8125rem',
                            color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                <strong style={{ color: 'var(--text-primary)' }}>Note:</strong>{' '}
                Prior Year figures populate automatically when you upload the new year's TB
                (the uploaded opening balances are the PY figures). Per-note disclosure details
                can be imported later via <em>Import from Previous Year</em> in each note.
              </div>
            </>
          )}
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={isCreating || loading}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleCreateClick}
            disabled={isCreating || loading || !spec || !!loadError}
          >
            <IconSystem.add size={16} />
            Create Engagement
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={createConfirmOpen}
        title="Create Next Year Engagement?"
        message={duplicate
          ? `A next-year engagement (${duplicate.existingEngagementCode}) already exists. Creating another will create a parallel engagement. Continue?`
          : "Confirm creation of the new FSA engagement with the carry-forward steps above?"}
        confirmLabel="Create"
        cancelLabel="Cancel"
        variant={duplicate ? 'danger' : 'default'}
        onConfirm={handleConfirmCreate}
        onCancel={() => setCreateConfirmOpen(false)}
      />
    </>
  );
};

export default FsaRollForwardConfirmModal;
